import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { isNullish } from "../../../lib/util/common-util";
import {
  Box,
  Button,
  chakra,
  ComponentMultiStyleConfig,
  FormControl,
  HStack,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import { ColorScheme } from "../../../lib/styles/types/color-scheme";
import { FileAttachableTextbox } from "../misc";
import { CMDummyTextareaButton } from "../../_components";

export type CMCommentBlockInputProps = {
  onSubmit: (values: FormValues) => void;
  placeholder: string | { shrunk: string; expanded: string } | undefined;
  fileUploadLabel: string;

  submitButton: {
    label: string;
    size: string;
    colorScheme: ColorScheme;
  };
  cancelButton: {
    label: string;
    size: string;
    colorScheme: ColorScheme;
  };
} & ComponentStyleProps;

export const CMCommentBlockInput = ({
  onSubmit,
  placeholder,
  fileUploadLabel,

  submitButton,
  cancelButton,

  ...rest
}: CMCommentBlockInputProps) => {
  const styles = useMultiStyleConfig("CMCommentBlockInputStyles", {});

  const [shrunkPlaceholder, expandedPlaceholder] = useMemo(() => {
    if (isNullish(placeholder)) return ["", ""];
    if (typeof placeholder === "string") return [placeholder, placeholder];
    return [placeholder.shrunk, placeholder.expanded];
  }, [placeholder]);

  const [expanded, setExpanded] = useState(false);
  const handleExpand = () => {
    setExpanded(true);
  };

  const defaultFormValues: FormValues = {
    text: "",
    files: [],
  };
  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const onCancel = () => {
    setExpanded(false);
    reset(defaultFormValues);
  };

  // 参考: https://chakra-ui.com/guides/integrations/with-hook-form
  return (
    <Box {...rest} __css={styles.container}>
      {expanded ? (
        <chakra.form
          onSubmit={handleSubmit(onSubmit)}
          w="100%"
          display="flex"
          flexFlow="column nowrap"
          alignItems="stretch"
        >
          <FormControl alignSelf="stretch">
            <FileAttachableTextbox<FormValues>
              control={control}
              textareaFieldPath="text"
              fileUploadFieldPath="files"
              placeholder={expandedPlaceholder}
              fileUploadLabel={fileUploadLabel}
              focusOnMount={true}
            />
          </FormControl>
          <HStack __css={styles.buttons}>
            <Button
              type="submit"
              size={submitButton.size}
              colorScheme={submitButton.colorScheme}
            >
              {submitButton.label}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              size={cancelButton.size}
              colorScheme={cancelButton.colorScheme}
            >
              {cancelButton.label}
            </Button>
          </HStack>
        </chakra.form>
      ) : (
        <CMDummyTextareaButton
          label={shrunkPlaceholder}
          onClick={handleExpand}
        />
      )}
    </Box>
  );
};

export const CMCommentBlockInputStyles: ComponentMultiStyleConfig = {
  parts: ["container", "textbox", "buttons"],
  baseStyle: {
    container: {},
    textbox: {},
    buttons: {
      alignSelf: "flex-start",

      mt: "2",
      ml: "1",

      gap: "1",
    },
  },
};

type FormValues = { text: string; files: File[] };
